import { useController } from 'react-hook-form';

import { WarningFilled } from '@mint-lib/icons';
import {
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  typographyVariants,
} from '@mint-lib/tw-components';

interface Props {
  fieldName: string;
  label?: string;
  placeholder: string;
  disabled?: boolean;
  dataTestId?: string;
  /** Test id for the trigger itself (e.g. to assert it's disabled). */
  triggerTestId?: string;
  /** Stretch to the container instead of the default 280px. */
  fullWidth?: boolean;
  /** Datadog action name for opening the select, forwarded unchanged to the trigger. */
  'data-dd-action-name'?: string;
  /** Datadog action name for picking any option, forwarded unchanged to each item. */
  optionActionName?: string;
  options: {
    label: string;
    value: string;
  }[];
}

export const SelectField = ({
  fieldName,
  label,
  placeholder,
  disabled,
  dataTestId,
  triggerTestId,
  fullWidth = false,
  'data-dd-action-name': actionName,
  optionActionName,
  options,
}: Props) => {
  const controller = useController({
    name: fieldName,
  });

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex w-full flex-row gap-1">
        {label && (
          <Label variant="label01" htmlFor={fieldName} color="secondary">
            {label}
          </Label>
        )}
      </div>
      <div data-testid={dataTestId}>
        <Select
          name={fieldName}
          value={controller.field.value}
          onValueChange={controller.field.onChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={fullWidth ? 'w-full' : 'w-[280px] text-text-placeholder'}
            size="medium"
            data-testid={triggerTestId}
            data-dd-action-name={actionName}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className={fullWidth ? undefined : 'w-[280px]'}>
            <SelectGroup>
              {options.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-dd-action-name={optionActionName}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      {controller.fieldState.error?.message && (
        <div className="flex items-center gap-1">
          <WarningFilled className="fill-error-main" />
          <p
            className={typographyVariants({
              variant: 'helperText01',
              color: 'error',
            })}
          >
            {controller.fieldState.error?.message}
          </p>
        </div>
      )}
    </div>
  );
};
